import { comparer, reaction } from 'mobx';
import type { ICustomFilter, IFilterSchema } from '../../ui/data-table/components/filters/filters.models';
import { filtersWithSelection, isSameSelection, selectionFromFilters } from './claim-responsibility-filters.util';
import type { ResponsibilityType } from './claim-responsibility-report.util';

/** The parts of a `DataTablePresenter` the link reads and writes — structural, so it is testable without a DOM. */
export interface IResponsibilityLinkedTable {
    filters: ICustomFilter[];
    validFilters: ICustomFilter[];
    updateFilters(filters: ICustomFilter[]): void;
}

/** The parts of `ClaimResponsibilityStore` the link reads and writes. */
export interface IResponsibilityLinkedSelection {
    selectedType: ResponsibilityType | undefined;
    selectedCategory: string | undefined;
    setSelection(type: ResponsibilityType | undefined, category?: string): void;
}

/**
 * Keep the widget's selection and the claims table's responsibility/category filters in step (FE-151), on
 * whichever console mounts both. Returns the function that stops it — call it on unmount.
 *
 * Two reactions, one per direction, each writing only when the other side actually differs, so neither change
 * bounces back:
 * - table → widget fires immediately: the table's filters are what was saved, so on load they decide the selection;
 * - widget → table replaces the two filters in one `updateFilters`, so the table fetches once, not per field.
 */
export function linkResponsibilityToTable(
    selection: IResponsibilityLinkedSelection,
    table: IResponsibilityLinkedTable,
    filterSchemas: IFilterSchema[],
): () => void {
    const fromTable = reaction(
        () => selectionFromFilters(table.validFilters),
        next => {
            if (!isSameSelection(next, { type: selection.selectedType, category: selection.selectedCategory })) {
                selection.setSelection(next.type, next.category);
            }
        },
        { fireImmediately: true, equals: comparer.structural },
    );
    const fromWidget = reaction(
        () => ({ type: selection.selectedType, category: selection.selectedCategory }),
        next => {
            if (!isSameSelection(next, selectionFromFilters(table.filters))) {
                table.updateFilters(filtersWithSelection(table.filters, next, filterSchemas));
            }
        },
        { equals: comparer.structural },
    );
    return () => {
        fromTable();
        fromWidget();
    };
}
